import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * glør Bundle-Rabatt-Wächter — global.
 *
 * Problem: Die Bundle-Logik (assets/glr-pdp-bundle.js, assets/glr-cart-set.js)
 * legt beim Add-All einen Rabattcode auf den Warenkorb. Entfernt der Kunde die
 * Bundle-Produkte später aus dem Cart-Drawer (oder wird der Warenkorb leer),
 * bleibt der Code am Warenkorb kleben und greift auf verbliebene/andere
 * Produkte → falscher Rabatt.
 *
 * Lösung: Dieses Skript läuft global (eingebunden über den glør-eigenen Hook
 * sections/glr-header.liquid, KEIN Horizon-Core). Es merkt sich, welchen Code
 * WIR gesetzt haben — die Bundle-Skripte hinterlegen ihn in sessionStorage unter
 * KEY. Bei jeder Warenkorb-Änderung (StandardEvents.cartLinesUpdate) wird der
 * Warenkorb geprüft: liegt keine Bundle-Position (_glr_bundle) mehr vor oder ist
 * der Warenkorb leer UND unser gemerkter Code hängt noch am Warenkorb, wird der
 * Code via POST /cart/update.js mit { discount: '' } geleert und der Drawer
 * aktualisiert. Fremde/legitime Codes anderer Aktionen werden nie angefasst.
 */

const KEY = 'glr_bundle_discount';

/** Basis-Routen — global von Horizon gesetzt; mit hartem Fallback. */
function routes() {
  const r = (typeof Theme !== 'undefined' && Theme && Theme.routes) ? Theme.routes : {};
  return {
    cartJs: (r.cart_url ? r.cart_url : '/cart') + '.js',
    cartUpdate: r.cart_update_url ? r.cart_update_url : '/cart/update.js',
  };
}

/** @returns {string[]} Section-IDs der Cart-Items (für den Section-Refresh). */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter((id) => Boolean(id));
}

/** Verhindert Doppel-Feuern / Endlosschleife durch das eigene Cart-Update. */
let busy = false;

/**
 * Prüft den Warenkorb und entfernt — falls nötig — NUR unseren gemerkten
 * Bundle-Rabattcode.
 */
async function runCheck() {
  if (busy) return;
  const code = (sessionStorage.getItem(KEY) || '').trim();
  if (!code) return; // kein gemerkter Code -> nichts tun (nie fremde Codes löschen)

  busy = true;
  try {
    const r = routes();
    const cartRes = await fetch(r.cartJs, { headers: { Accept: 'application/json' } });
    const cart = await cartRes.json();

    const items = Array.isArray(cart.items) ? cart.items : [];
    const empty = (cart.item_count || 0) === 0;
    const hasBundle = items.some((it) => it && it.properties && it.properties._glr_bundle);

    // Bundle noch im Warenkorb -> Code behalten, fertig.
    if (!empty && hasBundle) return;

    // Hängt unser Code aktuell noch am Warenkorb? (Cart-Level + Line-Level Titel)
    const appliedTitles = new Set();
    const apps = Array.isArray(cart.cart_level_discount_applications)
      ? cart.cart_level_discount_applications : [];
    apps.forEach((a) => { if (a && a.title) appliedTitles.add(String(a.title).toLowerCase()); });
    items.forEach((it) => {
      const dc = (it && Array.isArray(it.discounts)) ? it.discounts : [];
      dc.forEach((d) => { if (d && d.title) appliedTitles.add(String(d.title).toLowerCase()); });
    });
    const ourApplied = appliedTitles.has(code.toLowerCase());

    // Leerer Warenkorb: der Code bleibt sonst session-seitig kleben und greift
    // beim nächsten (fremden) Produkt -> hier immer räumen.
    // Sonst nur räumen, wenn nachweislich UNSER Code hängt (Regel: nie blind
    // alle Codes löschen).
    if (empty || ourApplied) {
      // Marker VOR dem Update entfernen -> keine Wiederholung / Endlosschleife.
      sessionStorage.removeItem(KEY);
      await clearDiscount(r);
    } else {
      // Code nicht mehr aktiv oder ein fremder Code liegt an -> nur den
      // veralteten Marker fallenlassen, sonst nichts anfassen.
      sessionStorage.removeItem(KEY);
    }
  } catch (e) {
    console.warn('[glr-bundle-discount-guard] Prüfung fehlgeschlagen:', e);
  } finally {
    busy = false;
  }
}

/**
 * Leert die Rabattcodes am Warenkorb ({ discount: '' }) und aktualisiert die
 * Cart-Sektionen/Drawer über dasselbe Event, das auch die Bundle-Skripte nutzen.
 * @param {{cartUpdate:string}} r
 */
async function clearDiscount(r) {
  const ids = cartSectionIds();
  const body = { discount: '' };
  if (ids.length) body.sections = ids.join(',');

  const res = await fetch(r.cartUpdate, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();

  // Drawer/Cart-Items neu rendern: externes CartLinesUpdateEvent mit den frisch
  // gerenderten Sektionen — component-cart-items morpht daraus (gleicher Weg wie
  // glr-cart-set.js / glr-pdp-bundle.js). Läuft best-effort.
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { sections: data.sections, source: 'glr-bundle-discount-guard', didError: false },
    });
  } catch (e) {
    // Kein Refresh möglich -> Code ist serverseitig bereits geleert; der Drawer
    // zieht spätestens bei der nächsten Interaktion nach.
  }
}

/**
 * Auf Warenkorb-Änderungen reagieren. Erst die (ggf. vorhandene) Promise der
 * auslösenden Mutation abwarten, damit /cart.js den finalen Stand liefert.
 * @param {CartLinesUpdateEvent} event
 */
async function onCartLinesUpdate(event) {
  if (busy) return; // eigenes Update ignorieren
  try { if (event && event.promise) await event.promise; } catch (e) { /* egal */ }
  runCheck();
}

if (!window.__glrBundleDiscountGuard) {
  window.__glrBundleDiscountGuard = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
  // Einmal beim Laden prüfen: falls ein früher gesetzter Code hängengeblieben
  // ist, ohne dass auf DIESER Seite ein Cart-Event feuert.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => runCheck(), { once: true });
  } else {
    runCheck();
  }
}
